import React from "react";
import { observer } from "mobx-react-lite";
import Select from "react-select";
import {
  InputGroup,
  Label
} from "reactstrap";
import {FormattedMessage} from "react-intl";
import {stripProtocol} from "../../../utils/common";
import {protocolConstants} from "../../../utils/constants";
import "../../codes/create/CreateCode.scss";

const HiddenSingleValue = () => null;

const PublishDomainSelectView = observer(({ publishDomainSelectState, configs, disabled = false, qrPreviewStore, codeState }) => {
  // Whether the tenant allows the user to CHANGE the protocol
  const protocolToggleEnabled = configs?.protocol_toggle_enabled === true;
  // Whether to RENDER the protocol select at all — we always show it when the stores
  // are wired up, so users can SEE which protocol will be used regardless of the toggle
  const showProtocolSelect = !!codeState;

  React.useEffect(() => {
    if (showProtocolSelect && codeState?.selectedProtocol && qrPreviewStore?.qrProtocol !== codeState.selectedProtocol) {
      qrPreviewStore?.setQrProtocol(codeState.selectedProtocol);
    }
  }, [showProtocolSelect, codeState?.selectedProtocol, qrPreviewStore]);

  const handleDomainChange = (selectedOption) => {
    const originalDomain = publishDomainSelectState.publishDomainStore.publishDomains.find(
      d => d.uuid === selectedOption.uuid
    );
    if (originalDomain) {
      publishDomainSelectState.selectPublishDomain(originalDomain);
    } else {
      publishDomainSelectState.selectPublishDomain(selectedOption);
    }
  };

  const currentProtocol = (showProtocolSelect ? (qrPreviewStore?.qrProtocol || codeState?.selectedProtocol) : null) || protocolConstants.HTTP;

  const domainSelect = (
    <Select
      sm={3}
      classNamePrefix="react-select"
      options={publishDomainSelectState?.publishDomainOptionValues()}
      id="selectedPublishDomain"
      value={publishDomainSelectState?.selectedPublishDomain || null}
      defaultValue={publishDomainSelectState.selectedPublishDomain ? publishDomainSelectState.selectedPublishDomain : publishDomainSelectState.getFirstPublishedDomain()}
      isSearchable
      onInputChange={(data) => publishDomainSelectState?.searchPublishDomain(data)}
      onChange={showProtocolSelect ? handleDomainChange : (data) => publishDomainSelectState?.selectPublishDomain(data)}
      getOptionLabel={option => showProtocolSelect ? currentProtocol + stripProtocol(option.label) : option.label}
      getOptionValue={option => option.uuid}
      placeholder="Select"
      isDisabled={disabled}
      className={showProtocolSelect ? "react-select-container protocol-domain-select" : "react-select-container"}
    />
  );

  return (
    <>
      {showProtocolSelect ? (
        <InputGroup>
          <Select
            className="react-select-container protocol-select"
            components={{
              SingleValue: HiddenSingleValue,
              IndicatorSeparator: () => null
            }}
            menuPlacement="auto"
            menuPosition="fixed"
            onChange={(option) => {
              qrPreviewStore?.setQrProtocol(option.value);
              codeState?.setSelectedProtocol?.(option.value);
            }}
            classNamePrefix="react-select"
            value={{
              label: currentProtocol === protocolConstants.HTTP ? "HTTP" : "HTTPS",
              value: currentProtocol
            }}
            options={[
              { label: "HTTP", value: protocolConstants.HTTP },
              { label: "HTTPS", value: protocolConstants.HTTPS }
            ]}
            // Disable the protocol select when the tenant hasn't enabled the toggle —
            // the user can still SEE the protocol that will be used, but can't change it
            isDisabled={disabled || !protocolToggleEnabled}
            isSearchable={false}
          />
          {domainSelect}
        </InputGroup>
      ) : (
        <>
          {domainSelect}
          { configs?.prefix_shorturl_enabled && publishDomainSelectState?.selectedPublishDomain?.prefixes?.length > 0 &&
          <>
            <Label sm={2} className="text-sm-right"><FormattedMessage id="createQr.domainPrefix.defaultMessage"/></Label>
            <Select
              sm={2}
              className="react-select-container"
              classNamePrefix="react-select"
              id="selectedPrefix"
              options={publishDomainSelectState?.prefixOptionValues()}
              value={publishDomainSelectState.selectedPublishDomain.selectedPrefix}
              defaultValue={publishDomainSelectState.selectedPublishDomain.selectedPrefix}
              onChange={(data) => publishDomainSelectState.selectedPublishDomain.selectPrefix(data)}
              getOptionLabel={option => option.label}
              getOptionValue={option => option.prefix}
              isDisabled={publishDomainSelectState?.selectedPublishDomain?.hasNoPrefixes}
            />
          </>
          }
        </>
      )}
    </>
  );
});

export default PublishDomainSelectView;
